import cx from 'classnames'
import { useState } from 'react'
import { WorkflowInstance } from '~/api/desktop/workflowInstances'
import TinyNotification from '~/components/notifications/TinyNotification'
import Table from '~/components/Table'
import { getOperatorName } from '~/core/appContextUtils'
import { useUserMetadata } from '~/core/MultiFrontendContext'
import { formatTimestamp } from '~/utils/date'
import cs from './pending_approvals_table.scss'
import WorkflowApprovalDetailsDialog from './WorkflowApprovalDetailsDialog'

interface PendingApprovalsTableProps {
  className?: string
  pendingWorkflows: WorkflowInstance[]
  loading: boolean
  onWorkflowUpdate: () => void
}

const PendingApprovalsTable = ({
  className,
  pendingWorkflows,
  loading,
  onWorkflowUpdate,
}: PendingApprovalsTableProps) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState<
    WorkflowInstance | undefined
  >()
  const [showApprovalDialog, setShowApprovalDialog] = useState(false)
  const userMetadata = useUserMetadata()
  const operatorName = getOperatorName(userMetadata) || ''

  const handleRowClicked = (workflow: WorkflowInstance) => {
    setSelectedWorkflow(workflow)
    setShowApprovalDialog(true)
  }

  const getStatusBadge = (workflow: WorkflowInstance) => {
    if (workflow.earliest_start_time) {
      const earliest = new Date(workflow.earliest_start_time)
      const now = new Date()
      if (earliest > now) {
        return <TinyNotification type='bareInfo' message='Scheduled' />
      }
    }
    return <TinyNotification type='bareInfo' message='Ready' />
  }

  const tableColumns = [
    {
      name: 'Workflow',
      width: 200,
      render: (row: WorkflowInstance) => row.workflow_definition_id,
    },
    {
      name: 'Requested By',
      width: 150,
      render: (row: WorkflowInstance) => row.requested_by || 'Unknown',
    },
    {
      name: 'Reason',
      width: 250,
      render: (row: WorkflowInstance) => (
        <div className={cs.reason}>
          {row.requested_reason || (
            <span className={cs.noReason}>No reason provided</span>
          )}
        </div>
      ),
    },
    {
      name: 'Requested At',
      width: 160,
      render: (row: WorkflowInstance) => formatTimestamp(row.requested_at),
    },
    {
      name: 'Status',
      width: 120,
      render: (row: WorkflowInstance) => getStatusBadge(row),
    },
  ]

  if (loading) {
    return (
      <div className={cx(className, cs.bigMessage)}>Loading pending approvals...</div>
    )
  }

  if (pendingWorkflows.length === 0) {
    return (
      <div className={cx(className, cs.bigMessage)}>No workflows pending approval.</div>
    )
  }

  return (
    <div className={cx(className, cs.pendingApprovalsTable)}>
      <Table<WorkflowInstance>
        columns={tableColumns}
        data={pendingWorkflows}
        className={cs.table}
        onRowClick={handleRowClicked}
        rowLeftRightPaddingVariant='rowPaddingLow'
        rowKey='uuid'
      />
      <WorkflowApprovalDetailsDialog
        workflowInstance={selectedWorkflow}
        isOpen={showApprovalDialog}
        onClose={() => {
          setShowApprovalDialog(false)
          setSelectedWorkflow(undefined)
        }}
        onApprovalAction={onWorkflowUpdate}
        operatorName={operatorName}
      />
    </div>
  )
}

export default PendingApprovalsTable
